export default function Products() {
  return (
    <section id="products" className="relative bg-background-50 py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider">
              <i className="ri-rocket-2-line"></i>
              In-house products
            </div>
            <h4 className="mt-5 font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">
              <a href="#products" className="hover:text-primary-700 transition-colors cursor-pointer">
                Two flagship platforms, purpose-built for the AI and RWA era.
              </a>
            </h4>
          </div>
          <p className="text-base text-foreground-600 md:max-w-sm leading-relaxed">
            Product engineering isn't a side project for us — it's how we prove our tech
            works at scale before your team ever touches it.
          </p>
        </div>

        {/* Omni AI */}
        <article className="relative rounded-lg overflow-hidden border border-background-200 bg-gradient-to-br from-primary-900 to-primary-700 text-background-50 mb-8 md:mb-10">
          <div className="absolute inset-0 bg-dot-pattern opacity-30" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 p-8 md:p-12 lg:p-16">
            <div className="flex flex-col">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/20 border border-accent-400/40 text-accent-300 text-xs font-semibold w-fit uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                Product 01 · Omni AI
              </div>
              <h4 className="mt-5 font-heading font-bold text-3xl md:text-4xl leading-tight">
                <a href="/products/omni-ai" className="hover:text-accent-300 transition-colors cursor-pointer">
                  One aggregator platform for every AI model you actually use.
                </a>
              </h4>
              <p className="mt-4 text-background-200 text-sm md:text-base leading-relaxed">
                Omni AI unifies frontier LLMs, image, video, code and agentic models behind
                one workspace with shared context, spend controls and governance. Inspired by
                the best of Skywork and AI Fiesta — built for enterprises that need one
                accountable pane of glass.
              </p>

              <ul className="mt-6 space-y-3">
                {[
                  'Access GPT, Claude, Gemini, Llama & vertical models from a single UI',
                  'Team workspaces, roles, audit logs and per-model budget guardrails',
                  'Agentic workflows, RAG over your data, and reusable prompt libraries',
                  'Deploy in Sky Edge cloud, GDB, or your own VPC for data residency',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-background-100">
                    <span className="mt-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-background-50 flex-shrink-0">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#contact" className="px-5 py-3 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2">
                  Request a demo <i className="ri-arrow-right-line"></i>
                </a>
                <a href="#contact" className="px-5 py-3 rounded-md bg-background-50/10 hover:bg-background-50/20 border border-background-50/30 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors">
                  Partner with us
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-lg overflow-hidden border border-background-50/20 bg-background-50/5 backdrop-blur">
                <img
                  src="https://readdy.ai/api/search-image?query=Sleek%20modern%20AI%20aggregator%20platform%20dashboard%20UI%20mockup%20on%20a%20dark%20navy%20background%20with%20a%20chat%20panel%2C%20multiple%20AI%20model%20cards%20in%20a%20left%20sidebar%2C%20orange%20and%20blue%20accent%20highlights%2C%20clean%20futuristic%20interface%20with%20isometric%20subtle%20lighting%2C%20stylized%20illustration%20suitable%20for%20product%20showcase%20hero%20image&width=1200&height=900&seq=omniai-dashboard-01&orientation=landscape"
                  alt="Omni AI aggregator platform dashboard preview"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 hidden md:flex items-center gap-3 bg-background-50 text-foreground-900 px-4 py-3 rounded-md border border-background-200">
                <div className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-sparkling-2-line"></i>
                </div>
                <div className="text-xs">
                  <div className="font-semibold">40+ models unified</div>
                  <div className="text-foreground-500">Single workspace · Single bill</div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* RWA Tokenization */}
        <article className="relative rounded-lg overflow-hidden border border-background-200 bg-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 p-8 md:p-12 lg:p-16">
            <div className="relative order-2 lg:order-1">
              <div className="relative rounded-lg overflow-hidden border border-background-200 bg-background-50">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20real%20estate%20building%20being%20tokenized%20into%20digital%20blockchain%20assets%20floating%20above%20it%2C%20connected%20by%20orange%20and%20navy%20blue%20light%20threads%2C%20on%20a%20light%20warm%20cream%20background%2C%20clean%20minimalist%20stylized%20artwork%2C%20financial%20technology%20aesthetic%2C%20subtle%20geometric%20cubes%2C%20suitable%20for%20fintech%20product%20showcase&width=1200&height=900&seq=rwa-tokenization-01&orientation=landscape"
                  alt="Real estate tokenization platform illustration"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -top-4 -left-4 hidden md:flex items-center gap-3 bg-primary-500 text-background-50 px-4 py-3 rounded-md">
                <div className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50/20">
                  <i className="ri-building-4-line"></i>
                </div>
                <div className="text-xs">
                  <div className="font-semibold">Issuance → Marketplace</div>
                  <div className="text-background-200">Compliance built-in</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold w-fit uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-600"></span>
                Product 02 · RWA Platform
              </div>
              <h4 className="mt-5 font-heading font-bold text-3xl md:text-4xl text-foreground-900 leading-tight">
                <a href="/products/rwa-tokenization" className="hover:text-accent-600 transition-colors cursor-pointer">
                  Real estate tokenization, from title deed to secondary trade.
                </a>
              </h4>
              <p className="mt-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                Our in-house RWA platform is a full lifecycle solution for real estate
                tokenization — the same use case pioneered by SettleMint, delivered as a
                turnkey product with issuance, cap table, KYC/AML, on-chain settlement,
                and a compliant secondary marketplace.
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: 'ri-file-shield-2-line', title: 'Issuance & Title Deed', desc: 'Legal wrapping, title deed verification, token standards' },
                  { icon: 'ri-user-star-line', title: 'KYC / AML', desc: 'Onboarding, whitelisting, jurisdictional rules' },
                  { icon: 'ri-store-2-line', title: 'Marketplace', desc: 'Primary + regulated secondary trading' },
                  { icon: 'ri-scales-3-line', title: 'Compliance Ops', desc: 'Reporting, audit trails, regulator dashboards' },
                ].map((f) => (
                  <div key={f.title} className="p-4 rounded-md bg-background-50 border border-background-200">
                    <div className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 mb-2">
                      <i className={`${f.icon} text-lg`}></i>
                    </div>
                    <div className="text-sm font-semibold text-foreground-900">{f.title}</div>
                    <div className="text-xs text-foreground-600 mt-0.5">{f.desc}</div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#contact" className="px-5 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2">
                  Talk to product team <i className="ri-arrow-right-line"></i>
                </a>
                <a href="#contact" className="px-5 py-3 rounded-md bg-background-50 hover:bg-background-100 border border-background-300 text-foreground-900 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors">
                  Download brief
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}